import { formatClock, type HealthLevel, type HealthSignal } from '@krp/client-runtime'

import type { ResourceContact } from './types'

/**
 * Связь с источником ресурса (SRC-HEALTH, `MyResource.contact`) одним сигналом точки «Связь» (RP-UI-LINK, шаг 3) — для
 * любого вида: кабинет AQSI, база 1С, будущие. Вид здесь не ветвится: служба вида сама ведёт `contact`, Platform отдаёт
 * его без изменений.
 */

/** Что показать человеку по коду причины: одна строка состояния и совет. Один словарь — копий в экранах нет. */
interface SourceReasonText {
  summary: string
  advice: string
}

/**
 * Тексты по коду причины: вид неудачи обращения (`lastError.kind`) или причина неизвестного состояния (`reasonCode`).
 * Дословный текст службы сюда не входит — он показывается как есть в `problem` (ADR-8).
 */
export const SOURCE_REASON_TEXTS: Readonly<Record<string, SourceReasonText>> = {
  network: {
    summary: 'Нет соединения с источником',
    advice: 'Проверьте, что источник включён и доступен из сети сервера. Связь проверится сама при следующем обращении.',
  },
  timeout: {
    summary: 'Источник не ответил вовремя',
    advice: 'Источник отвечает слишком медленно. Подождите и повторите действие; если повторяется — сообщите администратору.',
  },
  auth: {
    summary: 'Источник не принял учётные данные',
    advice: 'Администратору: проверьте логин и пароль источника в «Источниках». Повторять вход наугад не нужно.',
  },
  http: {
    summary: 'Источник ответил ошибкой',
    advice: 'Передайте администратору текст ошибки — кнопка «Скопировать» ниже.',
  },
  protocol: {
    summary: 'Отвечает не источник',
    advice: 'Вместо источника ответило что-то другое (прокси, страница входа). Администратору: проверьте адрес в «Источниках».',
  },
  'service-unavailable': {
    summary: 'Служба источника не отвечает',
    advice: 'Служба программы, которая работает с этим источником, недоступна. Сообщите администратору.',
  },
  'service-not-configured': {
    summary: 'Служба источника не подключена',
    advice: 'Служба для этого вида источника не подключена к программе. Сообщите администратору.',
  },
  'never-contacted': {
    summary: 'Связь ещё не проверялась',
    advice: 'Программа ещё не обращалась к источнику: состояние появится после первого обращения.',
  },
}

const DOWN_FALLBACK: SourceReasonText = {
  summary: 'Источник не отвечает',
  advice: 'Передайте администратору текст ошибки — кнопка «Скопировать» ниже.',
}

const UNKNOWN_FALLBACK: SourceReasonText = {
  summary: 'Связь ещё не проверялась',
  advice: 'Состояние появится после первого обращения программы к источнику.',
}

/** Причины неизвестного состояния, при которых работать с источником нельзя: молчит или не подключена НАША служба вида. */
const DOWN_REASONS: ReadonlySet<string> = new Set(['service-unavailable', 'service-not-configured'])

/** У вида нет службы и внешней связи (оборудование на компьютере кассира): о связи с источником сказать нечего. */
const NO_SERVICE = 'no-service'

/**
 * Уровень связи источника; `null` — сказать нечего (сервер не прислал `contact` или у вида нет службы).
 * - `ok` — источник отвечает;
 * - `down` — последнее обращение не удалось, или наша служба вида не отвечает / не подключена;
 * - `unknown` — ещё не обращались.
 */
export function sourceLevel(contact: ResourceContact | null): HealthLevel | null {
  if (contact === null) return null
  if (contact.state === 'ok') return 'ok'
  if (contact.state === 'error') return 'down'
  if (contact.reasonCode === NO_SERVICE) return null
  return contact.reasonCode !== null && DOWN_REASONS.has(contact.reasonCode) ? 'down' : 'unknown'
}

const textOf = (code: string | null | undefined, fallback: SourceReasonText): SourceReasonText =>
  (code == null ? undefined : SOURCE_REASON_TEXTS[code]) ?? fallback

/** Непустой текст или `null`: пустая строка службы — не причина. */
const nonEmpty = (value: string | null | undefined): string | null => (value == null || value === '' ? null : value)

/**
 * Текст для администратора: состояние, вид и код ошибки, время попытки и последнего ответа — моменты в ISO. Название
 * источника сюда не входит: мини-окно ставит его первой строкой само.
 */
function diagnosticsOf(contact: ResourceContact): string {
  const error = contact.lastError
  return [
    'Состояние: нет связи',
    ...(error !== null
      ? [
          `Ошибка: ${error.message}`,
          `Вид: ${error.kind}${error.code !== null ? `; HTTP ${error.code}` : ''}`,
          `Попытка: ${error.at}`,
        ]
      : []),
    ...(contact.reasonCode !== null ? [`Причина: ${contact.reasonCode}${contact.reason !== null ? ` — ${contact.reason}` : ''}`] : []),
    ...(contact.lastRequestAt !== null ? [`Последнее обращение: ${contact.lastRequestAt}`] : []),
    `Последний ответ: ${contact.lastSuccessAt ?? 'не было'}`,
  ].join('\n')
}

/**
 * Сигнал связи с источником для точки «Связь». `subject` — ключ и название источника на экране («Кабинет AQSI · Вольная
 * 28»); `null` — сказать нечего (см. {@link sourceLevel}). Время в строке — `formatClock` (пояс устройства).
 */
export function sourceHealth(contact: ResourceContact | null, subject: { id: string; title: string }): HealthSignal | null {
  const level = sourceLevel(contact)
  if (contact === null || level === null) return null
  const base = { subject: { kind: 'source' as const, id: subject.id, title: subject.title }, lastOkAt: contact.lastSuccessAt, actions: [] }

  if (level === 'ok') {
    return { ...base, level, summary: 'Отвечает', problem: null, since: null, advice: null, diagnostics: null }
  }

  if (level === 'unknown') {
    const text = textOf(contact.reasonCode, UNKNOWN_FALLBACK)
    return { ...base, level, summary: text.summary, problem: null, since: null, advice: text.advice, diagnostics: null }
  }

  // `down`: либо неудачное обращение службы к источнику, либо молчит/не подключена наша служба вида.
  const error = contact.state === 'error' ? contact.lastError : null
  const text = textOf(error?.kind ?? contact.reasonCode, DOWN_FALLBACK)
  const at = formatClock(error?.at ?? null)
  return {
    ...base,
    level,
    summary: at === null ? text.summary : `${text.summary} · проверено в ${at}`,
    problem: nonEmpty(error?.message) ?? nonEmpty(contact.reason),
    since: error?.at ?? null,
    advice: text.advice,
    diagnostics: diagnosticsOf(contact),
  }
}
